import type { ContentCheck, ContentIssue, Payload } from './types';
import { urlIssues } from './url';

/** Практический предел ввода: реальную ёмкость всё равно определяет encoder. */
export const MAX_INPUT_CHARS = 4296;

const utf8 = new TextEncoder();

export function utf8Length(value: string): number {
  return utf8.encode(value).length;
}

/** Есть ли в строке символы вне ASCII — влияет на выбор режима кодирования. */
export function hasNonAscii(value: string): boolean {
  for (const ch of value) {
    if (ch.codePointAt(0)! > 0x7f) return true;
  }
  return false;
}

const INVISIBLE = /[­​‌‍﻿]/;
const CR = /\r/;

/** Символы, которые легко потерять при копировании и которые стоит подсветить. */
function invisibleIssues(value: string): ContentIssue[] {
  const issues: ContentIssue[] = [];
  if (value !== value.trim() && value.trim() !== '') {
    issues.push({
      level: 'warning',
      code: 'edge-whitespace',
      message:
        'В начале или конце есть пробелы. Они будут закодированы как есть — мы ничего не убираем молча.',
      fix: { label: 'Убрать пробелы по краям', value: value.trim() },
    });
  }
  if (INVISIBLE.test(value)) {
    issues.push({
      level: 'warning',
      code: 'invisible-chars',
      message: 'Найдены невидимые символы (мягкий перенос или нулевой пробел). Они попадут в QR.',
    });
  }
  if (CR.test(value)) {
    issues.push({
      level: 'info',
      code: 'crlf',
      message: 'В тексте есть перевод строки CR/CRLF. Он сохраняется без изменений.',
    });
  }
  return issues;
}

/**
 * Единственная точка, которая решает, что именно уйдёт в encoder.
 * Ввод не переписывается: любое изменение предлагается как явный fix.
 */
export function checkContent(payload: Payload): ContentCheck {
  const raw = payload.value;
  const issues: ContentIssue[] = [];
  let host: string | null = null;

  if (raw === '') {
    return { raw, encoded: '', issues: [], host: null, ok: false };
  }

  const bytes = utf8Length(raw);
  if (raw.length > MAX_INPUT_CHARS) {
    issues.push({
      level: 'error',
      code: 'too-long',
      message: `Слишком длинный ввод: ${raw.length} символов. Реальная ёмкость зависит от режима, версии и уровня коррекции, но такой объём не поместится ни при каких настройках.`,
    });
  }

  issues.push(...invisibleIssues(raw));

  if (payload.kind === 'url') {
    const res = urlIssues(raw);
    issues.push(...res.issues);
    host = res.host;
  } else if (hasNonAscii(raw)) {
    issues.push({
      level: 'info',
      code: 'utf8-eci',
      message: `Текст содержит символы вне ASCII (${bytes} байт в UTF-8). Кодируем в UTF-8 с явным указанием ECI 26.`,
    });
  }

  const ok = !issues.some((i) => i.level === 'error');
  return { raw, encoded: raw, issues, host, ok };
}
